import { apiClient } from './api';

// Types
export type AccessStatusType = 'none' | 'pending' | 'approved' | 'rejected';

export interface B2bAccessRequest {
  id: string;
  organizationId: string;
  userId?: string;
  email: string;
  firstName: string;
  lastName: string;
  companyName: string;
  phone?: string;
  message?: string;
  status: 'pending' | 'approved' | 'rejected';
  reviewedById?: string;
  reviewedAt?: string;
  reviewNotes?: string;
  assignedTierId?: string;
  isBanned?: boolean;
  bannedUntil?: string;
  banReason?: string;
  createdAt: string;
  updatedAt: string;
  organization?: {
    id: string;
    name: string;
    b2bPortalSlug?: string;
  };
  assignedTier?: {
    id: string;
    name: string;
    discountPercentage: number;
  };
}

export interface ResellerProfile {
  id: string;
  userId: string;
  organizationId: string;
  companyName: string;
  isApproved: boolean;
  defaultTierId?: string;
  defaultTier?: {
    id: string;
    name: string;
    discountPercentage: number;
  };
}

export interface AccessStatusResponse {
  status: AccessStatusType;
  request?: B2bAccessRequest;
  resellerProfile?: ResellerProfile;
  canRequestAccess: boolean;
  bannedUntil?: string;
  banReason?: string;
}

export interface PortalInfo {
  organization: {
    id: string;
    name: string;
    b2bPortalSlug: string;
    b2bPortalSettings?: {
      welcomeMessage?: string;
      termsUrl?: string;
      requestAccessEnabled?: boolean;
    };
  };
  settings: {
    welcomeMessage?: string;
    termsUrl?: string;
    requestAccessEnabled?: boolean;
  };
}

export interface CreateAccessRequestDto {
  companyName: string;
  phone?: string;
  message?: string;
}

// B2B Portal API service
export const b2bService = {
  // Get portal info (public)
  async getPortalInfo(orgSlug: string): Promise<PortalInfo> {
    const response = await apiClient.get<PortalInfo>(`/b2b-portal/${orgSlug}/info`);
    return response.data;
  },

  // Get user's access status for a specific organization (requires auth)
  async getAccessStatus(orgSlug: string): Promise<AccessStatusResponse> {
    const response = await apiClient.get<AccessStatusResponse>(`/b2b-portal/${orgSlug}/my-access-status`);
    return response.data;
  },

  // Get all user's access requests across organizations (requires auth)
  async getMyAccessRequests(): Promise<B2bAccessRequest[]> {
    const response = await apiClient.get<B2bAccessRequest[]>('/b2b-portal/my-access-requests');
    return response.data;
  },

  // Request access to a B2B portal (requires auth)
  async requestAccess(orgSlug: string, data: CreateAccessRequestDto): Promise<B2bAccessRequest> {
    const response = await apiClient.post<B2bAccessRequest>(`/b2b-portal/${orgSlug}/request-access`, data);
    return response.data;
  },

  // Get B2B products (requires auth + approved access)
  async getProducts(orgSlug: string, params?: { page?: number; limit?: number }) {
    const response = await apiClient.get(`/b2b-portal/${orgSlug}/products`, { params });
    return response.data;
  },

  // Get B2B product detail (requires auth + approved access)
  async getProduct(orgSlug: string, productId: string) {
    const response = await apiClient.get(`/b2b-portal/${orgSlug}/products/${productId}`);
    return response.data;
  },

  // Get my quotes (requires auth + approved access)
  async getMyQuotes(orgSlug: string) {
    const response = await apiClient.get(`/b2b-portal/${orgSlug}/my-quotes`);
    return response.data;
  },

  // Get my invoices (requires auth + approved access)
  async getMyInvoices(orgSlug: string) {
    const response = await apiClient.get(`/b2b-portal/${orgSlug}/my-invoices`);
    return response.data;
  },

  // Get my profile (requires auth + approved access)
  async getMyProfile(orgSlug: string) {
    const response = await apiClient.get(`/b2b-portal/${orgSlug}/my-profile`);
    return response.data;
  },

  // Request a quote (requires auth + approved access)
  async createQuoteRequest(orgSlug: string, data: { items: { productId: string; quantity: number; notes?: string }[]; message?: string }) {
    const response = await apiClient.post(`/b2b-portal/${orgSlug}/quote-request`, data);
    return response.data;
  },
};
